import { Methods } from '../../api/methods.js';
import type { ParsedArgs } from '../args.js';
import { booleanFlag, flag, flagAll, hasFlag, numberFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import { type BinaryResponse, writeBase64Output } from '../files.js';
import type { CommandResult } from '../output.js';
import { kvArgs, pickDefined, rawRequest, request } from './common.js';

export async function mediaCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'search';
  switch (action) {
    case 'status':
      return request(args, Methods.Media);
    case 'search':
      return request(args, Methods.MediaSearch, {
        query: args.positionals[2],
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        maxResults: numberFlag(args.flags, 'max-results'),
        cursor: flag(args.flags, 'cursor'),
        tags: many(args, 'tag'),
        letter: flag(args.flags, 'letter'),
      });
    case 'browse':
      return request(args, Methods.MediaBrowse, {
        path: args.positionals[2] ?? flag(args.flags, 'path'),
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        maxResults: numberFlag(args.flags, 'max-results'),
        cursor: flag(args.flags, 'cursor'),
        letter: flag(args.flags, 'letter'),
        sort: flag(args.flags, 'sort'),
      });
    case 'browse-index':
      return request(args, Methods.MediaBrowseIndex, {
        path: args.positionals[2] ?? flag(args.flags, 'path'),
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        sort: flag(args.flags, 'sort'),
      });
    case 'active':
      return request(args, Methods.MediaActive, { slot: flag(args.flags, 'slot') });
    case 'active-update':
      return request(args, Methods.MediaActiveUpdate, {
        systemId: requiredFlag(args, 'system-id'),
        mediaPath: requiredFlag(args, 'media-path'),
        mediaName: requiredFlag(args, 'media-name'),
      });
    case 'history':
      return request(args, Methods.MediaHistory, {
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        limit: numberFlag(args.flags, 'limit'),
        cursor: flag(args.flags, 'cursor'),
      });
    case 'history-latest':
      return request(args, Methods.MediaHistoryLatest);
    case 'top':
      return request(args, Methods.MediaHistoryTop, {
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        since: flag(args.flags, 'since'),
        limit: numberFlag(args.flags, 'limit'),
      });
    case 'lookup': {
      const name = flag(args.flags, 'name') ?? args.positionals[2];
      const system = flag(args.flags, 'system');
      if (!name || !system) {
        throw new CliError('media lookup requires <name> --system <system>', ExitCode.Usage);
      }
      return request(args, Methods.MediaLookup, {
        name,
        system,
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
      });
    }
    case 'meta':
      return request(args, Methods.MediaMeta, mediaReference(args));
    case 'meta-update':
      return request(args, Methods.MediaMetaUpdate, {
        ...mediaReference(args),
        media: {
          launcherOverride: hasFlag(args.flags, 'clear-launcher')
            ? null
            : requiredFlag(args, 'launcher'),
        },
      });
    case 'image': {
      const output = flag(args.flags, 'output');
      if (!output) throw new CliError('media image requires --output <path>', ExitCode.Usage);
      const result = await rawRequest<BinaryResponse>(args, Methods.MediaImage, {
        ...mediaReference(args),
        imageTypes: many(args, 'image-type'),
        maxSize: numberFlag(args.flags, 'max-size'),
      });
      return { data: writeBase64Output(result, output) };
    }
    case 'tags':
      return request(args, Methods.MediaTags, {
        systems: many(args, 'system'),
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
      });
    case 'tags-update': {
      const add = many(args, 'add');
      const remove = many(args, 'remove');
      if (!add && !remove) {
        throw new CliError('media tags-update requires --add or --remove', ExitCode.Usage);
      }
      return request(args, Methods.MediaTagsUpdate, {
        ...mediaReference(args),
        add,
        remove,
      });
    }
    case 'title-parse':
      return request(args, Methods.MediaTitleParse, {
        systemId: requiredFlag(args, 'system-id'),
        path: requiredFlag(args, 'path'),
      });
    case 'clean-orphans':
      return request(args, Methods.MediaCleanOrphans);
    case 'control': {
      const controlAction = args.positionals[2];
      if (!controlAction) {
        throw new CliError('media control requires <action>', ExitCode.Usage);
      }
      return request(args, Methods.MediaControl, {
        action: controlAction,
        slot: flag(args.flags, 'slot'),
        args: kvArgs(flagAll(args.flags, 'arg')),
      });
    }
    case 'index':
      return mediaIndexCommand(args);
    case 'scrapers':
      return request(args, Methods.Scrapers);
    case 'scrape':
      return mediaScrapeCommand(args);
    default:
      throw new CliError(`Unknown media action "${action}"`, ExitCode.Usage);
  }
}

async function mediaIndexCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'status';
  switch (action) {
    case 'start': {
      const systems = many(args, 'system');
      const rebuild = hasFlag(args.flags, 'rebuild') ? true : undefined;
      if (rebuild && systems) {
        throw new CliError('--rebuild cannot be combined with --system', ExitCode.Usage);
      }
      return request(args, Methods.MediaGenerate, {
        systems,
        fuzzySystem: booleanFlag(args.flags, 'fuzzy-system'),
        rebuild,
      });
    }
    case 'cancel':
      return request(args, Methods.MediaGenerateCancel);
    case 'resume':
      return request(args, Methods.MediaGenerateResume);
    case 'status': {
      const data = await rawRequest<{ database?: unknown }>(args, Methods.Media);
      return { data: data.database ?? data };
    }
    default:
      throw new CliError(`Unknown media index action "${action}"`, ExitCode.Usage);
  }
}

async function mediaScrapeCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'status';
  switch (action) {
    case 'start':
      return request(args, Methods.MediaScrape, {
        scraperId: requiredFlag(args, 'scraper'),
        systems: many(args, 'system'),
        force: hasFlag(args.flags, 'force') ? true : undefined,
      });
    case 'status':
      return request(args, Methods.MediaScrapeStatus);
    case 'cancel':
      return request(args, Methods.MediaScrapeCancel);
    case 'resume':
      return request(args, Methods.MediaScrapeResume);
    default:
      throw new CliError(`Unknown media scrape action "${action}"`, ExitCode.Usage);
  }
}

function many(args: ParsedArgs, name: string): string[] | undefined {
  const values = flagAll(args.flags, name);
  return values.length > 0 ? values : undefined;
}

function requiredFlag(args: ParsedArgs, name: string): string {
  const value = flag(args.flags, name);
  if (!value) throw new CliError(`--${name} is required`, ExitCode.Usage);
  return value;
}

function mediaReference(args: ParsedArgs): Record<string, unknown> {
  const mediaId = numberFlag(args.flags, 'media-id');
  if (mediaId !== undefined && (!Number.isInteger(mediaId) || mediaId <= 0)) {
    throw new CliError('--media-id must be a positive integer', ExitCode.Usage);
  }
  const system = flag(args.flags, 'system');
  const path = flag(args.flags, 'path');
  if (mediaId === undefined && (!system || !path)) {
    throw new CliError('Provide --media-id or both --system and --path', ExitCode.Usage);
  }
  return pickDefined({ mediaId, system, path }) ?? {};
}
